import api from 'services/api';
import { enforce, nullable, num, object, runSchema, str } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import type { SaveLock } from './postLock';

// PUT /locks/:zoneName/:lockName — update. Backend re-SELECTs the row joined to
// zones (so `zone_name` comes from the DB, and — unlike POST — there is no
// `zone_id`) and enriches it with `folder_path` from the shared serializer.
//
// `UpdatedLock` is its OWN type + schema for the same reason as `CreatedLock`:
// a distinct backend statement whose shape happens to overlap. Structural
// typing lets it flow wherever a `Lock` is expected downstream.
export interface UpdatedLock {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_name: string;
  readonly limit: number;
  readonly limit_allocation: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
}

const updatedSchema = object<UpdatedLock>({
  id: num(),
  name: str(),
  description: nullable(str()),
  zone_name: str(),
  limit: num(),
  limit_allocation: num(),
  created_at: str(),
  updated_at: str(),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
});

/**
 * PUT `/locks/:zoneName/:lockName`. The URL params identify the *original*
 * (pre-edit) lock; the body carries the new values, which may rename the lock
 * or move it to another zone. Returns the updated record. Throws on HTTP error
 * or structural validation failure.
 */
export const putLock = async (original: { zoneName: string; name: string }, save: SaveLock): Promise<UpdatedLock> => {
  const res = await api.put(`/locks/${encodeURIComponent(original.zoneName)}/${encodeURIComponent(original.name)}`, save);
  const r = runSchema(updatedSchema, res.data, 'putLock');
  if (!r.ok) {
    throw new Error('putLock: response failed validation');
  }
  return enforce(r.value, r.warnings, ['id']);
};
